import React, { FC, useState, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TextField, Form, requiredItem, VirtualizedComboBox } from '@eta/components';
import { IDropdownOption } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import {
    AddressDto
} from '../../ProviderManagement/Service/proxy';
import { ReduxState } from '../../common/reducers';
import './IntermediaryAddress.scss';
import { isEmptyText } from '../../helpers/utils';

export interface IntermediaryAddressProps {
    address?: AddressDto;
    setAddress: (address: AddressDto) => void;
    form: React.MutableRefObject<Form>;
}

const mapStateToProps = (state: ReduxState) => ({
    currentLanguage: state.reduxlanguage.language,
    lastError: state.profile.lastError,
    referenceData: state.referenceData?.taxpayersConfig
        ? {
            optionsCity:
                state.referenceData?.taxpayersConfig?.cities?.map<
                    IDropdownOption
                >((v) => {
                    return {
                        key: v.id || 0,
                        text: v.name || '',
                        data: v.parentCode || ''
                    };
                }) ?? [],
            optionsGovernorate:
                state.referenceData?.taxpayersConfig?.governorates?.map<
                    IDropdownOption
                >((v) => {
                    return {
                        key: v.id || 0,
                        text: `${v.name}` || '',
                        data: v.code || ''
                    };
                }) ?? [],
            optionsCountry:
                state.referenceData?.taxpayersConfig?.countries?.map<
                    IDropdownOption
                >((v) => {
                    return {
                        key: v.id || 0,
                        text: `${v.name}` || '',
                        data: `${v.code}`
                    };
                }) ?? [],
        }
        : null,
});
const mapDispatchToProps = {
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export const IntermediaryAddress: FC<PropsFromRedux & IntermediaryAddressProps> = (
    props: PropsFromRedux & IntermediaryAddressProps
) => {
    const { t } = useTranslation(['resources', 'common']);
    const {
        referenceData,
        address,
        setAddress,
        form
    } = props;
    const [currentAddress, setFormAddress] = useState(address ?? new AddressDto());
    const [CityFilter, setCityFilter] = useState<IDropdownOption[] | undefined>();

    const countryCode = (addressDto: AddressDto) =>{
        if(isEmptyText(addressDto.countryCode)){
            const country = referenceData?.optionsCountry.find((x) => x?.key === addressDto?.countryID);
            return country?.data;
        }
        return addressDto.countryCode;
    };

    const setCurrentAddress = (addressDto: AddressDto) => {
        const add = addressDto;
        setAddress(add);
        setFormAddress(add);
    };

    useEffect(() => {
        const GovernorateCode = referenceData?.optionsGovernorate.find((x) => x?.key === currentAddress?.governorateID);
        const filterCity = referenceData?.optionsCity.filter((item) => item?.data === GovernorateCode?.data);
        setCityFilter(filterCity);
        currentAddress.countryCode = countryCode(currentAddress);
        const cityId = filterCity?.filter((i) => i.key === currentAddress.cityID);
        if(cityId && cityId.length === 0)
            currentAddress.cityID = undefined;
    }, [currentAddress, referenceData]);

    return (
        <div>
            <div className="bodyLine2s">
                <div className="bodyLine2s">
                    <VirtualizedComboBox
                        selectedKey={currentAddress?.countryID}
                        options={referenceData?.optionsCountry ?? []}
                        label={t('form.TaxPayer.Country')}
                        onChange={(_, v) =>
                            setCurrentAddress(
                                new AddressDto({
                                    ...currentAddress,
                                    countryID: Number(v?.key) ?? '',
                                    countryCode: v?.data,
                                    cityID: 0,
                                    governorateID: 0,
                                    foreignCityName: '',
                                    foreignGovernorateName: '',
                                })
                            )
                        }
                        required
                        validationFunc={(v) => requiredItem(t, v, t('form.TaxPayer.Country'))}
                        onValidationChange={(isValid) => {
                            form.current.SetValidity('countryID', isValid);
                        }
                        }
                        multiSelectDelimiter=''
                        allowFreeform
                        useComboBoxAsMenuWidth
                    />
                    {currentAddress.countryCode === 'EG' || currentAddress.countryID === 1 ? (
                        <VirtualizedComboBox
                            selectedKey={currentAddress?.governorateID}
                            options={referenceData?.optionsGovernorate ?? []}
                            label={t('form.TaxPayer.Governorate')}
                            onChange={(_, v) => {
                                setCurrentAddress(
                                    new AddressDto({
                                        ...currentAddress,
                                        governorateID: Number(v?.key) ?? '',
                                        foreignGovernorateName: ''
                                    })
                                );
                                const filterCity = referenceData?.optionsCity.filter((item) => item.data === v?.data);
                                setCityFilter(filterCity);
                            }
                            }
                            required
                            validationFunc={(v) => requiredItem(t, v, t('form.TaxPayer.Governorate'))}
                            onValidationChange={(isValid) => {
                                form.current.SetValidity('governorateID', isValid);

                            }
                            }
                            multiSelectDelimiter=''
                            allowFreeform
                            useComboBoxAsMenuWidth
                        />

                    ) : (
                            <TextField
                                label={t('form.TaxPayer.Governorate')}
                                value={currentAddress?.foreignGovernorateName}
                                onChange={(_, v) =>
                                    setCurrentAddress(
                                        new AddressDto({
                                            ...currentAddress,
                                            foreignGovernorateName: v ?? '',
                                            governorateID: undefined
                                        })
                                    )
                                }
                                required
                                validationFunc={(v) => requiredItem(t, v, t('form.TaxPayer.Governorate'))}
                                onValidationChange={(isValid) => {
                                    form.current.SetValidity('foreignGovernorateName', isValid);
                                }
                                }
                                maxLength={50}
                            />
                        )}
                </div>
                <div className="bodyLine2s">
                    {currentAddress.countryCode === 'EG' || currentAddress.countryID === 1 ? (
                        <VirtualizedComboBox
                            selectedKey={currentAddress?.cityID}
                            options={CityFilter ?? []}
                            label={t('form.TaxPayer.City')}
                            onChange={(_, v) =>
                                setCurrentAddress(
                                    new AddressDto({
                                        ...currentAddress,
                                        cityID: Number(v?.key) ?? '',
                                        foreignCityName: ''
                                    })
                                )
                            }
                            required
                            validationFunc={(v) => requiredItem(t, v, t('form.TaxPayer.City'))}
                            onValidationChange={(isValid) => {
                                form.current.SetValidity('cityID', isValid);
                            }
                            }
                            multiSelectDelimiter=''
                            allowFreeform
                            useComboBoxAsMenuWidth
                        />
                    ) : (
                            <TextField
                                label={t('form.TaxPayer.City')}
                                value={currentAddress?.foreignCityName}
                                onChange={(_, v) =>
                                    setCurrentAddress(
                                        new AddressDto({
                                            ...currentAddress,
                                            foreignCityName: v ?? '',
                                            cityID: undefined
                                        })
                                    )
                                }
                                required
                                validationFunc={(v) => requiredItem(t, v, t('form.TaxPayer.City'))}
                                onValidationChange={(isValid) => {
                                    form.current.SetValidity('foreignCityName', isValid);
                                }
                                }
                                maxLength={50}
                            />
                        )}
                </div>
            </div>
            <div className="bodyLine2s padding">
                <TextField
                    label={t('form.TaxPayer.BuildingName')}
                    value={currentAddress?.buildingNo}
                    onChange={(_, v) =>
                        setCurrentAddress(
                            new AddressDto({
                                ...currentAddress,
                                buildingNo: v ?? '',
                            })
                        )
                    }
                    required
                    validationFunc={(v) => requiredItem(t, v, t('form.TaxPayer.BuildingName'))}
                    onValidationChange={(isValid) => {
                        form.current.SetValidity('buildingNo', isValid);
                    }
                    }
                    maxLength={50}
                />
                <div className="bodyLine2s">
                    <div className="bodyLine2s">
                        <TextField
                            label={t('form.TaxPayer.FlatNo')}
                            value={currentAddress?.flatNo}
                            onChange={(_, v) =>
                                setCurrentAddress(
                                    new AddressDto({
                                        ...currentAddress,
                                        flatNo: v ?? '',
                                    })
                                )
                            }
                            maxLength={5}
                        />
                        <TextField
                            label={t('form.TaxPayer.FloorNo')}
                            value={currentAddress?.floorNo}
                            onChange={(_, v) =>
                                setCurrentAddress(
                                    new AddressDto({
                                        ...currentAddress,
                                        floorNo: v ?? '',
                                    })
                                )
                            }
                            maxLength={5}
                        />
                    </div>
                </div>
            </div>
            <div className="bodyLine2s padding">
                <TextField
                    label={t('form-components.StreetName')}
                    value={currentAddress?.streetName}
                    onChange={(_, v) =>
                        setCurrentAddress(
                            new AddressDto({
                                ...currentAddress,
                                streetName: v ?? '',
                            })
                        )
                    }
                    required
                    validationFunc={(v) => requiredItem(t, v, t('form-components.StreetName'))}
                    onValidationChange={(isValid) => {
                        form.current.SetValidity('streetName', isValid);
                    }
                    }
                    maxLength={200}
                />
                <TextField
                    label={t('form.TaxPayer.Street2')}
                    value={currentAddress?.streetName2}
                    onChange={(_, v) =>
                        setCurrentAddress(
                            new AddressDto({
                                ...currentAddress,
                                streetName2: v ?? '',
                            })
                        )
                    }
                    maxLength={100}
                />
            </div>
            <div className="bodyLine2s padding">
                <TextField
                    label={t('form.TaxPayer.AdditionalInformation')}
                    value={currentAddress?.additionalInformation}
                    onChange={(_, v) =>
                        setCurrentAddress(
                            new AddressDto({
                                ...currentAddress,
                                additionalInformation: v ?? '',
                            })
                        )
                    }
                    maxLength={200}
                />
                <div className="bodyLine2s">
                    <TextField
                        label={t('form.TaxPayer.PostalCode')}
                        value={currentAddress?.postalCode}
                        onChange={(_, v) =>
                            setCurrentAddress(
                                new AddressDto({
                                    ...currentAddress,
                                    postalCode: v ?? '',
                                })
                            )
                        }
                        maxLength={10}
                    />
                </div>
            </div>
        </div>
    );
};

export default connector(IntermediaryAddress);
